/**
 * accountMergeActions.ts — Accounts S6 (0141): the merged toast with its Undo action
 * and the Undo call the survivor's page reuses (AccountMerge.tsx holds the
 * dialog). Undo works from the merge record at any time; its answer names the
 * rows that had moved since and were left where they are.
 */
import { toast } from 'sonner'
import { undoAccountMerge } from '../../api'
import type { SalesAccountMerge } from '../../../../api/types'
import { apiErrorMessage } from '../../../../api/client'

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

/** "1 contact and 2 deals" — the two things a merge moves. */
export function movedPhrase(contacts: number, deals: number): string {
  return `${plural(contacts, 'contact')} and ${plural(deals, 'deal')}`
}

/** Undo one merge from its record and say what happened, naming any row that
 *  had moved since the merge (the server leaves it where it is). */
export async function undoMerge(mergeId: string, mergedName: string, onChanged: () => void): Promise<boolean> {
  try {
    const u = await undoAccountMerge(mergeId)
    const left = u.movedSince
    const names = left.map(m => m.name || (m.kind === 'contact' ? 'a contact' : 'a deal')).join(', ')
    toast.success(`Merge undone: "${mergedName}" is its own account again`, left.length > 0
      ? { description: `${left.length === 1 ? 'One record had' : `${left.length} records had`} moved since the merge and stayed where ${left.length === 1 ? 'it is' : 'they are'} now: ${names}.` }
      : undefined)
    onChanged()
    return true
  } catch (e) {
    toast.error(apiErrorMessage(e, 'Failed to undo the merge'))
    return false
  }
}

/** The merged toast, with Undo as its action. */
export function announceMerge(rec: SalesAccountMerge, survivorName: string, onChanged: () => void) {
  toast.success(`Merged "${rec.mergedName}" into "${survivorName}"`, {
    description: `${movedPhrase(rec.movedContactIds.length, rec.movedDealIds.length)} moved.`,
    action: { label: 'Undo', onClick: () => { void undoMerge(rec.id, rec.mergedName, onChanged) } },
  })
}
